/**
 * Stops a second size of the same product going into the cart by accident.
 *
 * Every add route asks guard() first. If the cart already holds a different
 * variant of that product the add is held back and the dialog opens; the
 * shopper either goes ahead, in which case the original add runs untouched,
 * or closes it and nothing is added.
 *
 * A sale is never blocked by a failure here: if the cart cannot be read, or
 * anything else goes wrong, the add proceeds.
 *
 * Markup is snippets/size-nudge.liquid. Copy and the size guide URL live
 * there, from locale keys and theme settings.
 */
(function () {
  'use strict';

  var CART_URL = '/cart.js';
  var FORM_SELECTOR = 'form[data-type="add-to-cart-form"]';
  var BYPASS = 'sizeNudgeBypass';

  /**
   * Is a different variant of this product already in the cart?
   * @param {string|number} productId
   * @param {string|number} variantId
   * @returns {Promise<boolean>} false on any failure, so the add goes ahead
   */
  function otherSizeInCart(productId, variantId) {
    if (!productId || !variantId) return Promise.resolve(false);

    return fetch(CART_URL, { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        return (cart.items || []).some(function (item) {
          return (
            String(item.product_id) === String(productId) &&
            String(item.variant_id) !== String(variantId)
          );
        });
      })
      .catch(function () {
        return false;
      });
  }

  function dialogComponent() {
    return document.querySelector('dialog-component.size-nudge');
  }

  /**
   * The product page's own size guide control, above the size options. The
   * Smartsize app injects it, so it arrives after this script runs and is
   * looked up each time rather than cached. The later selectors are there so
   * a theme-side guide would be picked up too, if one is ever added.
   */
  function pageSizeGuideTrigger() {
    return document.querySelector(
      '.smartsize-trigger, [data-size-guide], .size-guide-trigger'
    );
  }

  /** Open the page's own size guide, or fall back to the page in settings. */
  function openSizeGuide(component, button) {
    var trigger = pageSizeGuideTrigger();

    if (typeof component.closeDialog === 'function') component.closeDialog();

    if (trigger) {
      // The app's trigger is a div with role="button", so a click is the only
      // way in. Let the dialog finish closing first: it holds a scroll lock
      // and the focus trap, which would otherwise fight the guide's own.
      setTimeout(function () {
        var clickable = trigger.closest('[role="button"], button, a') || trigger;
        clickable.click();
      }, 250);
      return;
    }

    var url = button.dataset.sizeGuideUrl;
    if (url) window.location.href = url;
  }

  /**
   * Hold the add back if another size is already in the cart.
   * @param {string|number} productId
   * @param {string|number} variantId
   * @param {() => void} add - performs the add the shopper asked for
   */
  function guard(productId, variantId, add) {
    otherSizeInCart(productId, variantId).then(function (clash) {
      var component = dialogComponent();

      if (!clash || !component || typeof component.showDialog !== 'function') {
        add();
        return;
      }

      var confirm = component.querySelector('[data-size-nudge-add]');

      if (confirm) {
        // Replaced rather than added to, so repeated opens cannot stack
        // handlers and add the item more than once.
        confirm.onclick = function () {
          if (typeof component.closeDialog === 'function') component.closeDialog();
          add();
        };
      }

      var guide = component.querySelector('[data-size-nudge-guide]');

      if (guide) {
        // Show it when the page has a guide of its own, even if no fallback
        // page is set in theme settings.
        if (pageSizeGuideTrigger()) guide.hidden = false;

        guide.onclick = function () {
          openSizeGuide(component, guide);
        };
      }

      component.showDialog();
    });
  }

  window.MaebeSizeNudge = { guard: guard, otherSizeInCart: otherSizeInCart };

  /**
   * The product form, used by the PDP and by Horizon's quick add modal, which
   * lifts this same form out of the fetched product page.
   *
   * Bound on window, in the capture phase. The theme delegates its own
   * handlers from document with { capture: true } (assets/component.js) and
   * registers them first, so a document-level listener here would run second
   * and the add would already be away. Capture reaches window before
   * document, which puts this first whatever the script order.
   *
   * Going ahead re-submits the form with a flag this listener ignores, so the
   * theme adds to cart and opens the drawer exactly as it normally would.
   */
  window.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form || !form.matches || !form.matches(FORM_SELECTOR)) return;
      if (form.dataset[BYPASS] === 'true') return;

      var variantField = form.querySelector('[name="id"]');
      var productField = form.querySelector('[name="product-id"]');
      if (!variantField || !productField) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      guard(productField.value, variantField.value, function () {
        form.dataset[BYPASS] = 'true';
        if (typeof form.requestSubmit === 'function') form.requestSubmit();
        else form.submit();
        delete form.dataset[BYPASS];
      });
    },
    true
  );
})();
